import React, { Component, ReactNode } from 'react';
import { telemetry, SpanStatusCode } from '@/telemetry-browser';
import { trace } from '@opentelemetry/api';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

/**
 * React Error Boundary with OpenTelemetry instrumentation
 * Automatically captures and traces React component errors
 */
export class TelemetryErrorBoundary extends Component<Props, State> {
  private span?: any;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Start a span for the error
    this.span = telemetry.startSpan('react.error_boundary', {
      attributes: {
        'error.name': error.name,
        'error.message': error.message,
        'error.stack': error.stack || '',
        'react.component_stack': errorInfo.componentStack,
        'react.error_boundary': true,
      },
    });

    // Record the exception
    this.span.recordException(error);
    
    // Set error status
    this.span.setStatus({
      code: SpanStatusCode.ERROR,
      message: `React Error: ${error.message}`,
    });

    // End the span
    this.span.end();

    // Also log to console for development
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback || (
        <div className="flex flex-col items-center justify-center min-h-screen p-8 text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <h2 className="text-xl font-semibold text-red-800 mb-4">
              Something went wrong
            </h2>
            <p className="text-red-600 mb-4">
              An error occurred in the application. The error has been logged and our team has been notified.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC to wrap components with telemetry error boundary
 */
export function withTelemetryErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) {
  const WrappedComponent = (props: P) => (
    <TelemetryErrorBoundary fallback={fallback}>
      <Component {...props} />
    </TelemetryErrorBoundary>
  );

  WrappedComponent.displayName = `withTelemetryErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}

/**
 * Hook to manually record errors with telemetry
 */
export function useErrorTelemetry() {
  const recordError = React.useCallback((error: Error, context?: Record<string, any>) => {
    const span = telemetry.startSpan('manual.error', {
      attributes: {
        'error.name': error.name,
        'error.message': error.message,
        'error.stack': error.stack || '',
        'error.manual': true,
        ...context,
      },
    });

    span.recordException(error);
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: error.message,
    });
    span.end();
  }, []);

  return { recordError };
}